import {Component, computed, inject, model} from '@angular/core'
import {ILoan} from '@sparbanken-syd/loan-renewal-be'
import {MatFormField, MatInput, MatLabel, MatSuffix} from '@angular/material/input'
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms'
import {DateTime} from 'luxon'
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker'
import {OndemandService} from '../../../services/ondemand.service'
import {MatButton} from '@angular/material/button'
import {MatDialog, MatDialogActions, MatDialogClose} from '@angular/material/dialog'
import {FormatNumberDirective, IWaitComponentData, WaitComponent} from '@sparbanken-syd/sparbanken-syd-theme'
import {MatOption, MatSelect} from '@angular/material/select'

@Component({
  selector: 'spb-loan-display',
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatButton,
    FormatNumberDirective,
    MatDialogActions,
    MatDialogClose,
    MatSelect,
    MatOption
  ],
  templateUrl: './loan-display.component.html',
  styleUrl: './loan-display.component.scss'
})
export class LoanDisplayComponent {
  public loan = model.required<ILoan | null>()

  public form = computed(() => {
    // This is the next renewal date.
    const renewalDate = DateTime.fromMillis(this.loan()!.loan.renewalDate)
    const renewalControl = new FormControl<DateTime>(renewalDate, {nonNullable: true})

    const lastChangeDate = DateTime.fromMillis(this.loan()!.loan.lastRenewalDate ?? renewalDate.toMillis())

    const currentDebt = this.loan()!.loan.currentDebt
    const interest = this.loan()!.loan.interest
    const repaymentAmount = this.loan()!.loan.repaymentAmount

    // Bindningstid.
    const binding = this.loan()!.product.frequency
    const bindingControl = new FormControl(binding, {nonNullable: true})

    return new FormGroup({
      // Next villkorsändringsdag
      renewalDate: renewalControl,
      // Senaste villkorsändring
      lastRenewalDate: new FormControl<DateTime>(lastChangeDate, {nonNullable: true}),
      binding: bindingControl,
      currentDebt: new FormControl<number>(currentDebt, {nonNullable: true}),
      repaymentAmount: new FormControl<number>(repaymentAmount, {nonNullable: true}),
      interest: new FormControl<number>(interest, {nonNullable: true})
    })
  })

  private readonly service = inject(OndemandService)
  private readonly dialog = inject(MatDialog)

  public send(preview: boolean) {
    const loan = this.loan()!
    const form = this.form().controls

    const nonAdjustableLoans = new Set([
      'l760', 'l765', 'l770', // Business loans
      'l750', 'l755' // House loans
    ])
    loan.product.frequency = form.binding.value

    if (loan.product.frequency.includes('må') && !nonAdjustableLoans.has(loan.product.code)) {
      loan.loan.renewalDate = form.renewalDate.value.minus({months: 9}).toMillis()
    } else {
      loan.loan.renewalDate = form.renewalDate.value.toMillis()
    }
    loan.loan.lastRenewalDate = form.lastRenewalDate.value.toMillis()
    loan.loan.currentDebt = form.currentDebt.value
    loan.loan.interest = form.interest.value
    loan.loan.repaymentAmount = form.repaymentAmount.value

    const ref = this.dialog.open(WaitComponent, {
      data: {
        title: 'Bearbetar',
        text: !preview ? 'Skapar och skickar villkorsbilaga...' : 'Skapar dokument',
        progress: true
      },
      disableClose: true
    })
    this.service.preBound(this.loan()!, preview)
      .subscribe(res => {
        ref.close()
        this.dialog.open<WaitComponent, IWaitComponentData, void>(WaitComponent, {
          data: {
            title: 'Klart',
            closable: true,
            html: `<p>Visa <a href="${res.url}" target="_blank">dokument.</a></p>`
          }
        })
      })
  }
}
